/**
 * The technicians: real human figures, from real rigged models.
 *
 * Box-and-sphere people were tried and read as horrors, and a figure that
 * fails is worse than no figure. These are CC0 human bodies from Quaternius
 * - one female, one male - with four clips from the matching animation
 * library, assembled into public/models/technician*.glb (see the LICENSE
 * note beside them). They are the only files the fab loads: everything
 * else in the hall is procedural, and the rule behind that - nothing gated
 * behind hardware, works offline, loads fast on a school network - holds
 * for two 0.7 MB files served from the same origin as the app.
 *
 * THE GOWN IS A SHADER. The bodies are skin-tight meshes, and painted white
 * they read as naked. A cleanroom coverall is loose, so the body's vertices
 * are pushed out along their normals before skinning - the same mesh,
 * inflated a few centimetres into a baggy suit that still moves with every
 * bone - and the skin underneath is not drawn. The head inflates into the
 * hood; the eyes are hidden inside it, and goggles and a mask sit on the
 * outside where the face is. A front zipper and elastic cuffs at the
 * wrists and ankles, and the covers over the shoes, are the rest of what a
 * gown shows, each parented to its bone so it moves with the limb.
 *
 * Three stand at HMIs, working the screen and talking; one walks the aisle
 * and back, stopping for the student the way the robots do. The models load
 * after the hall is built, so the scene compiles their programs when they
 * arrive (FabCanvas handles that) rather than the first time one is drawn.
 */

import * as THREE from 'three'
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js'
import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js'
import { aisleDirection, FAB, outerAisleZ, SECOND_ROW_PIVOT_Z, TOOLS, toolFaceZ, type Tool } from './layout'
import { MAINTENANCE_PANEL, MAINTENANCE_TOOL } from './bays'
import { hasSecondRowCopy, hmiFaceX } from './builders'
import { BALLROOM } from './hall'
import { CAST, type Body, type CastMember } from './cast'
import { captureRestPose, relaxIdlePose, type RestPose } from './relaxIdle'

export interface Person {
  /** Who this is (cast.ts). */
  id: string
  group: THREE.Group
  mixer: THREE.AnimationMixer
  /** The walker's lane and heading; standing figures have none. */
  walker: { z: number; dir: 1 | -1; speed: number; stopped: boolean } | null
  /** The standing figures' clip rotation. */
  timer: number
  /** Stays in one clip: the technician inside the open panel. */
  fixed: boolean
  clips: Record<string, THREE.AnimationAction>
  current: string
  /** The hands as modelled, for the idle relaxation (relaxIdle.ts). */
  rest: RestPose
  /**
   * In conversation with the student: turned to face the eye, and kept
   * facing it, in the talking clip while a reply is being spoken and the
   * idle one while listening. The walker stops for it.
   */
  engaged: boolean
  talking: boolean
  /** Where they were facing, and what they were doing, before the talk. */
  home: { yaw: number; clip: string }
}

export interface People {
  group: THREE.Group
  people: Person[]
  advance(dt: number, eye: THREE.Vector3): void
  colliders(): Array<{ minX: number; maxX: number; minZ: number; maxZ: number }>
  /** Turn this person to the student and hold them in conversation. */
  engage(id: string): void
  /** End the conversation: they turn back and pick up what they were doing. */
  disengage(): void
  /** Whether the engaged person is speaking (talking clip) or listening. */
  setTalking(on: boolean): void
  /** The person in conversation, if any. */
  engaged(): Person | null
  /** Who a hit object belongs to: the person id, or null. */
  personOf(object: THREE.Object3D): string | null
  /**
   * Where someone is standing, and for the walker which way along the
   * aisle they were headed (+1 toward the gas farm, -1 toward gowning).
   * `dir` is 0 for anyone who stays put. Read from the walk state rather
   * than the model's rotation, since an engaged person is turned to face
   * the student and is no longer pointing the way they were going.
   */
  whereIs(id: string): { x: number; z: number; dir: number } | null
}

/**
 * How fast each clip runs, against a clock rather than against taste.
 *
 * The idle loop is one breath and it is 2.5 s long, which played at
 * source speed is 24 breaths a minute - about double a resting adult, and
 * it reads as a room full of people who have just run up the stairs. A
 * person standing at a screen breathes 12 to 14 times a minute, so the
 * loop wants to last four and a half seconds.
 *
 * The others are not breathing and are not slowed as far: talking gestures
 * genuinely are quicker than breath, and a hand going to a screen is a
 * deliberate movement, not an idle one. The walk cycle is left alone -
 * its speed is tied to WALK_SPEED below, and slowing it would slide the
 * feet across the floor.
 */
const CLIP_SPEED: Record<string, number> = {
  Idle_Loop: 0.55,
  Idle_Talking_Loop: 0.8,
  Interact: 0.9,
  Walk_Loop: 1,
}

/** The walker's lane: the aisle, between the robot lane and the centre. */
const WALK_Z = -0.6
const WALK_SPEED = 1.05
// Sixteen metres short of each end wall - the same 52 m the hall was
// tuned with, and the same margin in a packed ballroom.
// A function: the hall's length follows the layout and the switch is live.
const walkEnd = (): number => FAB.length / 2 - 16

/**
 * How far the gown stands off the body, metres: at the hood and limbs, and
 * over the torso, where a coverall bags and the body's shape is lost. The
 * torso band is in bind-pose height (the body is 1.8 m tall), blended in
 * over the hips and out below the shoulders.
 */
const GOWN_LOOSENESS = 0.028

/**
 * How much of the body's own surface direction the gown keeps over the
 * torso, and where that band sits.
 *
 * Standing the gown off the skin along the body's normals made a LOOSER
 * BODY, not a coverall: the mesh under it is a heroic game character, and
 * pushing its surface out 6 cm kept every line of the abdominal relief and
 * the pectoral shelf, so the technicians read as muscular people painted
 * white. A real bunny suit destroys that information - what you see is a
 * bagged cylinder with a person somewhere inside it.
 *
 * So the shading normal over the torso is turned toward the RADIAL
 * direction from the body's own axis, which is the normal a bag hanging off
 * the shoulders actually has. The relief is still in the silhouette, where
 * it belongs; it is out of the shading, where it was doing the damage. The
 * same turned normal drives the standoff above, so the torso inflates
 * toward a cylinder rather than toward a bigger copy of itself.
 */
const GOWN_BAG = 0.72

function gownMaterial(torso: number): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({ color: 0xf3f5f7, roughness: 0.9, metalness: 0 })
  m.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader.replace(
      '#include <beginnormal_vertex>',
      [
        '#include <beginnormal_vertex>',
        '{',
        'float bag = smoothstep(0.74, 0.96, position.y) * (1.0 - smoothstep(1.34, 1.54, position.y));',
        // The axis runs up through the model's origin, which is between the
        // feet: radial is the horizontal direction out from it. The epsilon
        // keeps a vertex sitting exactly on the axis from normalising zero.
        'vec3 radial = normalize(vec3(position.x, 0.0, position.z) + vec3(1e-4, 0.0, 1e-4));',
        `objectNormal = normalize(mix(objectNormal, radial, ${GOWN_BAG.toFixed(2)} * bag));`,
        '}',
      ].join('\n'),
    )
    shader.vertexShader = shader.vertexShader.replace(
      '#include <begin_vertex>',
      [
        '#include <begin_vertex>',
        '{',
        'float torso = smoothstep(0.78, 0.98, position.y) * (1.0 - smoothstep(1.36, 1.52, position.y));',
        `float loose = mix(${GOWN_LOOSENESS.toFixed(3)}, ${torso.toFixed(3)}, torso);`,
        'transformed += objectNormal * loose;',
        '}',
      ].join('\n'),
    )
  }
  // Each looseness is its own program; say so, or three would share one.
  m.customProgramCacheKey = () => `gown-${torso}`
  return m
}

/**
 * The two bodies in the free pack are both heroic - the woman is 1.77 m to
 * the man's 1.81 m and as broad - and under a hood and a bagged gown they
 * were the same figure. Real gowned colleagues are told apart by build and
 * height, so the women stand at 0.93 scale (1.64 m) in a gown that bags
 * less over the torso and keeps a waist.
 */
const BODY: Record<Body, { scale: number; gown: THREE.MeshStandardMaterial }> = {
  f: { scale: 0.93, gown: gownMaterial(0.042) },
  m: { scale: 1.0, gown: gownMaterial(0.065) },
}
/** Goggle lens, and the blue of mask, gloves, cuffs and shoe covers. */
const VISOR = new THREE.MeshStandardMaterial({ color: 0x1f262e, roughness: 0.15, metalness: 0.4 })
const TRIM = new THREE.MeshStandardMaterial({ color: 0x5b82ad, roughness: 0.75 })
const ZIP = new THREE.MeshStandardMaterial({ color: 0x9aa4ae, roughness: 0.5, metalness: 0.4 })

/** Fetch and assemble the technicians. Resolves to nothing if no model loads. */
export async function loadPeople(urls: Record<Body, string>): Promise<People | null> {
  const loader = new GLTFLoader()
  const loaded: Partial<Record<Body, GLTF>> = {}
  await Promise.all(
    (Object.keys(urls) as Body[]).map(async (body) => {
      try {
        loaded[body] = await loader.loadAsync(urls[body])
      } catch (err) {
        console.warn(`technician model ${body} unavailable:`, err)
      }
    }),
  )
  const any = loaded.f ?? loaded.m
  if (!any) return null
  const g = new THREE.Group()
  const people: Person[] = []

  const make = (who: CastMember): Person => {
    const fig = gownFigure(loaded[who.body] ?? any, who.body)
    // The pick walks up from whatever mesh the ray hit to this.
    fig.group.userData.personId = who.id
    return {
      id: who.id,
      group: fig.group,
      mixer: fig.mixer,
      walker: null,
      timer: 0,
      fixed: false,
      clips: fig.clips,
      current: '',
      engaged: false,
      talking: false,
      home: { yaw: 0, clip: who.clip },
      rest: fig.rest,
    }
  }
  const play = (p: Person, name: string, fade = 0.4): void => {
    const next = p.clips[name]
    if (!next || p.current === name) return
    const prev = p.clips[p.current]
    next.reset().setEffectiveTimeScale(CLIP_SPEED[name] ?? 1).setEffectiveWeight(1).fadeIn(fade).play()
    if (prev) prev.fadeOut(fade)
    p.current = name
  }

  return assemble(make, play, g, people)
}

/** A gowned body, animated, before it is anybody in particular. */
export interface GownedFigure {
  group: THREE.Group
  mixer: THREE.AnimationMixer
  clips: Record<string, THREE.AnimationAction>
  /** The hands as modelled, for the idle relaxation (relaxIdle.ts). */
  rest: RestPose
}

/**
 * Dress one body in a cleanroom gown: the bagged suit, the hood with
 * goggles and mask, the zipper and collar and badge, gloves and cuffs and
 * shoe covers, each piece on its bone. Exported so the chase crew
 * (chaseCrew.ts) wears the same gown - the chase is entered from the aisle
 * through a door in the bulkhead, without degowning, so the people in it
 * are dressed exactly as the people outside it.
 */
export function gownFigure(gltf: GLTF, body: Body): GownedFigure {
  {
    const model = cloneSkeleton(gltf.scene) as THREE.Group
    model.updateMatrixWorld(true)
    // The eyes, in the bind pose: where the goggles go. Then hidden - they
    // are inside the hood.
    const eyeBox = new THREE.Box3()
    model.traverse((o) => {
      const mesh = o as THREE.Mesh
      if (!mesh.isMesh) return
      mesh.frustumCulled = false
      if (/eye/i.test((mesh.material as THREE.Material).name)) {
        eyeBox.expandByObject(mesh)
        mesh.visible = false
      } else {
        mesh.material = BODY[body].gown
        mesh.castShadow = true
      }
    })
    const eyes = eyeBox.getCenter(new THREE.Vector3())
    const group = new THREE.Group()
    group.add(model)
    const mixer = new THREE.AnimationMixer(model)
    const clips: Record<string, THREE.AnimationAction> = {}
    for (const clip of gltf.animations) clips[clip.name] = mixer.clipAction(clip)

    // The gowning, parented to the bones so it turns with the limb: each
    // piece is placed in the bind pose in world terms - centred between two
    // bones, pointed from one to the other - and then attached, which keeps
    // that placement and lets the bone carry it from there.
    const at = (name: string): THREE.Vector3 | null => {
      const bone = model.getObjectByName(name)
      return bone ? bone.getWorldPosition(new THREE.Vector3()) : null
    }
    const attach = (boneName: string, mesh: THREE.Mesh, position: THREE.Vector3, forward: THREE.Vector3): void => {
      const bone = model.getObjectByName(boneName)
      if (!bone) return
      mesh.position.copy(position)
      mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), forward.clone().normalize())
      group.add(mesh)
      bone.attach(mesh)
    }
    /** A cuff: a short tube whose axis runs along the limb. */
    const cuff = (rTop: number, rBottom: number, length: number): THREE.Mesh => {
      const geo = new THREE.CylinderGeometry(rTop, rBottom, length, 16, 1, true)
      geo.rotateX(Math.PI / 2)
      return new THREE.Mesh(geo, TRIM)
    }
    const ahead = new THREE.Vector3(0, 0, 1)
    const clear = GOWN_LOOSENESS + 0.015
    // Goggles and mask, proud of the hood over the face.
    attach('Head', new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.065, 0.06), VISOR), eyes.clone().add(new THREE.Vector3(0, 0, clear + 0.01)), ahead)
    // The mask: the lower face from just under the goggles to under the
    // chin, the full width of the face so it meets the hood at the cheeks -
    // half an ellipsoid, wide and shallow, its open side against the face.
    // (A half-sphere in three spans z >= 0: it already bulges forward.)
    const mask = new THREE.Mesh(new THREE.SphereGeometry(1, 16, 10, 0, Math.PI), TRIM)
    mask.scale.set(0.098, 0.072, 0.052)
    attach('Head', mask, eyes.clone().add(new THREE.Vector3(0, -0.105, clear - 0.025)), ahead)
    // The zipper, collar to hips, in two runs so it bends with the spine;
    // the collar where hood meets suit; and the badge on the chest.
    const hips = at('spine_01')
    const chest = at('spine_03')
    const neck = at('neck_01')
    if (hips && chest && neck) {
      const lowerLen = chest.y - hips.y + 0.02
      const lower = new THREE.Mesh(new THREE.BoxGeometry(0.014, lowerLen, 0.008), ZIP)
      attach('spine_01', lower, hips.clone().lerp(chest, 0.5).add(new THREE.Vector3(0, 0, 0.12 + clear)), ahead)
      const upperLen = neck.y - chest.y
      const upper = new THREE.Mesh(new THREE.BoxGeometry(0.014, upperLen, 0.008), ZIP)
      attach('spine_03', upper, chest.clone().lerp(neck, 0.5).add(new THREE.Vector3(0, 0, 0.125 + clear)), ahead)
      const collar = new THREE.Mesh(new THREE.TorusGeometry(0.085 + clear, 0.012, 8, 24), TRIM)
      collar.geometry.rotateX(Math.PI / 2)
      attach('neck_01', collar, neck.clone().add(new THREE.Vector3(0, 0.03, 0.01)), ahead)
      const badge = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.085, 0.006), new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.5 }))
      attach('spine_03', badge, chest.clone().add(new THREE.Vector3(0.09, 0.08, 0.115 + clear)), ahead)
    }
    for (const side of ['l', 'r'] as const) {
      const forearm = at(`lowerarm_${side}`)
      const wrist = at(`hand_${side}`)
      const knuckle = at(`middle_01_${side}`)
      if (forearm && wrist && knuckle) {
        // Glove: a flattened ellipsoid along the hand, wrist to knuckles.
        const glove = new THREE.Mesh(new THREE.SphereGeometry(1, 12, 8), TRIM)
        glove.scale.set(0.055, 0.035, 0.09)
        attach(`hand_${side}`, glove, wrist.clone().lerp(knuckle, 0.6), knuckle.clone().sub(wrist))
        // The sleeve's elastic cuff, around the wrist along the forearm.
        attach(`hand_${side}`, cuff(0.05, 0.062, 0.06), wrist.clone().lerp(forearm, 0.08), wrist.clone().sub(forearm))
      }
      const shin = at(`calf_${side}`)
      const ankle = at(`foot_${side}`)
      const ball = at(`ball_${side}`)
      if (shin && ankle && ball) {
        // Shoe cover: an elongated ellipsoid over the foot, ankle to ball.
        const along = ball.clone().sub(ankle)
        const cover = new THREE.Mesh(new THREE.SphereGeometry(1, 14, 10), TRIM)
        cover.scale.set(0.065, 0.05, 0.15)
        attach(`foot_${side}`, cover, ankle.clone().lerp(ball, 0.6).add(new THREE.Vector3(0, -0.035, 0)), along)
        // The trouser's elastic cuff, around the ankle along the shin.
        attach(`foot_${side}`, cuff(0.062, 0.068, 0.07), ankle.clone().lerp(shin, 0.1), ankle.clone().sub(shin))
      }
    }
    // Everything above was placed at the model's own size; the whole
    // figure, gowning and all, scales together.
    group.scale.setScalar(BODY[body].scale)
    return { group, mixer, clips, rest: captureRestPose(model) }
  }
}

/** The clip speeds, for whoever plays the same clips on the same bodies. */
export const clipSpeed = (name: string): number => CLIP_SPEED[name] ?? 1

/**
 * Where the screen hangs along a tool's face, in the face's local x.
 *
 * THE BUILDER'S NUMBER, not a guess at it. builders.ts hangs the HMI arm at
 * the front end's outboard corner on any tool that has a front end - a real
 * screen swings off the end of the EFEM, clear of the port doors - and only
 * a tool without one keeps it at `w/2 - 0.4`. People used to be stood at
 * `w/2 - 0.4` on every tool, which on a tool with an EFEM put the technician
 * a metre and a half along the face, in front of a load port, talking to
 * nobody's screen. The floor-wear pass (builders.ts, the standing-spot blot)
 * already used this formula; now the person stands on the patch it wears.
 */
export const hmiLocalX = (tool: Tool): number => hmiFaceX(tool)

/**
 * Where a technician posted at a tool's HMI stands, and which way they face.
 *
 * A step back from the face, and a small side-step so the body does not sit
 * square between the camera and the screen. The face's local x runs against
 * world x on the +z side, which is the `facing` sign (bays.ts). Exported so
 * `npm run talk` can hold every posted technician to their own screen on
 * both halls, without standing a model up.
 */
export function hmiStandingSpot(tool: Tool): { x: number; z: number; yaw: number } {
  const facing = aisleDirection(tool)
  const lx = hmiLocalX(tool)
  return {
    x: tool.position[0] + (facing > 0 ? -lx : lx) + 0.2,
    z: toolFaceZ(tool) + facing * 1.5,
    yaw: facing > 0 ? Math.PI : 0,
  }
}

/**
 * The cast, placed and driven. Split from `loadPeople` only so that the
 * gowning above could be lifted out between them.
 */
function assemble(
  make: (who: CastMember) => Person,
  play: (p: Person, name: string, fade?: number) => void,
  g: THREE.Group,
  people: Person[],
): People {
  for (const who of CAST) {
    // The chase crew is stood by chaseCrew.ts, on the other side of the wall.
    if (who.post === 'chase') continue
    if (who.post === 'hmi') {
      const tool = TOOLS.find((t) => t.id === who.toolId)
      if (!tool) continue
      const p = make(who)
      // At the HMI, where the builder hung it, a step back from the face.
      const spot = hmiStandingSpot(tool)
      p.group.position.set(spot.x, 0, spot.z)
      // Face the tool: the model looks down its local +z.
      p.group.rotation.y = spot.yaw
      p.home.yaw = p.group.rotation.y
      p.timer = Math.random() * 6
      play(p, who.clip, 0)
      p.mixer.update(Math.random() * 2)
      g.add(p.group)
      people.push(p)
    } else if (who.post === 'maintenance') {
      // The maintenance technician: standing at the open panel of the tool
      // that is down, working inside it (bays.ts draws the panel, the stool
      // and the bag). ON THE FLOOR - he used to stand on top of the step
      // stool, which he has no reason to do and which read as a man
      // balanced on a crate in the middle of the aisle.
      const tool = TOOLS.find((t) => t.id === MAINTENANCE_TOOL)
      if (!tool) continue
      const p = make(who)
      const facing = aisleDirection(tool)
      const x = tool.position[0] + (facing > 0 ? -MAINTENANCE_PANEL.x : MAINTENANCE_PANEL.x)
      const z = toolFaceZ(tool) + facing * 0.62
      p.group.position.set(x, 0, z)
      p.group.rotation.y = facing > 0 ? Math.PI : 0
      p.home.yaw = p.group.rotation.y
      p.fixed = true
      play(p, who.clip, 0)
      p.mixer.update(1.1)
      g.add(p.group)
      people.push(p)
    } else {
      // The walker, starting mid-hall.
      const p = make(who)
      p.walker = { z: WALK_Z, dir: 1, speed: WALK_SPEED, stopped: false }
      p.group.position.set(-20, 0, WALK_Z)
      p.group.rotation.y = Math.PI / 2
      p.home.yaw = p.group.rotation.y
      play(p, who.clip, 0)
      // Posed from the first frame, so a reduced-motion session (which
      // never advances the mixer) still sees a person and not a T-pose.
      p.mixer.update(0.4)
      g.add(p.group)
      people.push(p)
    }
  }

  // Fab B's outer aisles are worked too: on each side, a technician at the
  // screen of one of the second row's copies and a walker in the aisle.
  // EXTRAS, NOT CAST. They wear the cast's bodies and clips but carry no
  // name, no post and no brief, and they cannot be clicked - `personId` is
  // left off, so the pick falls through them - because a technician who
  // can be spoken to has to be able to say where they are and what their
  // tool is doing, and a copy is not a tool (builders.ts `buildSecondRow`).
  if (BALLROOM) {
    const templates = CAST.filter((who) => who.post === 'hmi')
    const walkerTemplate = CAST.find((who) => who.post === 'walking') ?? templates[0]
    const extra = (template: CastMember | undefined, id: string): Person | null => {
      if (!template) return null
      const p = make({ ...template, id, toolId: null })
      delete p.group.userData.personId
      return p
    }
    ;[1, -1].forEach((side, i) => {
      const copies = TOOLS.filter((t) => Math.sign(t.position[2]) === side && hasSecondRowCopy(t))
      const tool = copies[Math.min(copies.length - 1, side > 0 ? 2 : 5)]
      const p = tool ? extra(templates[i % templates.length], `extra-hmi-${side}`) : null
      if (tool && p) {
        // The copy is the tool turned half a turn about the service aisle's
        // centreline at its own x, so its screen is where the original's
        // is, mirrored in x about the tool and in z about the pivot.
        const spot = hmiStandingSpot(tool)
        p.group.position.set(2 * tool.position[0] - spot.x, 0, 2 * side * SECOND_ROW_PIVOT_Z - spot.z)
        p.group.rotation.y = spot.yaw + Math.PI
        p.home.yaw = p.group.rotation.y
        p.timer = Math.random() * 6
        play(p, p.home.clip, 0)
        p.mixer.update(Math.random() * 2)
        g.add(p.group)
        people.push(p)
      }
      const w = extra(walkerTemplate, `extra-walker-${side}`)
      if (w) {
        const lane = side * (outerAisleZ() + 0.6)
        const dir = side > 0 ? -1 : 1
        w.walker = { z: lane, dir, speed: WALK_SPEED, stopped: false }
        w.group.position.set(side * 14, 0, lane)
        w.group.rotation.y = dir > 0 ? Math.PI / 2 : -Math.PI / 2
        w.home.yaw = w.group.rotation.y
        play(w, w.home.clip, 0)
        w.mixer.update(0.4)
        g.add(w.group)
        people.push(w)
      }
    })
  }

  /** Turn toward a yaw, no faster than a person turns. */
  const turnToward = (p: Person, target: number, dt: number, rate = 2.5): boolean => {
    let d = target - p.group.rotation.y
    d = Math.atan2(Math.sin(d), Math.cos(d))
    p.group.rotation.y += Math.sign(d) * Math.min(Math.abs(d), rate * dt)
    return Math.abs(d) < 0.02
  }

  /** People turning back to their post after a conversation. */
  const returning = new Set<Person>()

  /** The idle's fists and elbows, brought down - see relaxIdle.ts. */
  const relax = (p: Person): void => relaxIdlePose(p.group, p.rest, p.clips.Idle_Loop?.getEffectiveWeight() ?? 0)

  const advance = (dt: number, eye: THREE.Vector3): void => {
    for (const p of people) {
      p.timer += dt
      if (p.engaged) {
        // Face the student, and keep facing them as they move: the model
        // looks down its local +z, so the yaw is the bearing to the eye.
        const dx = eye.x - p.group.position.x
        const dz = eye.z - p.group.position.z
        turnToward(p, Math.atan2(dx, dz), dt, 3.0)
        play(p, p.talking ? 'Idle_Talking_Loop' : 'Idle_Loop', 0.3)
        p.mixer.update(dt)
        relax(p)
        continue
      }
      if (returning.has(p)) {
        if (turnToward(p, p.home.yaw, dt)) {
          returning.delete(p)
          play(p, p.home.clip, 0.4)
          if (p.walker) p.walker.stopped = false
        }
        p.mixer.update(dt)
        relax(p)
        continue
      }
      if (p.walker) {
        const w = p.walker
        // Stop for a student in the way, a couple of metres ahead.
        const ax = p.group.position.x + w.dir * 1.6
        const dx = eye.x - ax
        const dz = eye.z - w.z
        const blocked = eye.y > 0 && dx * dx + dz * dz < 1.8 * 1.8
        if (blocked !== w.stopped) {
          w.stopped = blocked
          play(p, blocked ? 'Idle_Loop' : 'Walk_Loop', 0.3)
        }
        if (!w.stopped) {
          p.group.position.x += w.dir * w.speed * dt
          if (Math.abs(p.group.position.x) > walkEnd()) {
            w.dir = w.dir > 0 ? -1 : 1
          }
          // Heading: the model walks down its local +z.
          const target = w.dir > 0 ? Math.PI / 2 : -Math.PI / 2
          let d = target - p.group.rotation.y
          d = Math.atan2(Math.sin(d), Math.cos(d))
          p.group.rotation.y += Math.sign(d) * Math.min(Math.abs(d), 2.5 * dt)
        }
      } else if (!p.fixed && p.timer > 9) {
        // Working the screen, then a word with someone, then a rest.
        p.timer = 0
        const order = ['Interact', 'Idle_Talking_Loop', 'Idle_Loop']
        const i = (order.indexOf(p.current) + 1) % order.length
        play(p, order[i])
      }
      p.mixer.update(dt)
      relax(p)
    }
  }

  const colliders = () =>
    people
      .filter((p) => !p.walker)
      .map((p) => ({
        minX: p.group.position.x - 0.35,
        maxX: p.group.position.x + 0.35,
        minZ: p.group.position.z - 0.35,
        maxZ: p.group.position.z + 0.35,
      }))

  let inTalk: Person | null = null
  const engage = (id: string): void => {
    const p = people.find((q) => q.id === id)
    if (!p || inTalk === p) return
    disengage()
    inTalk = p
    returning.delete(p)
    p.engaged = true
    p.talking = false
    // The walker's heading is wherever he was going; the others' home is
    // fixed at placement.
    if (p.walker) p.home.yaw = p.walker.dir > 0 ? Math.PI / 2 : -Math.PI / 2
  }
  const disengage = (): void => {
    const p = inTalk
    if (!p) return
    inTalk = null
    p.engaged = false
    p.talking = false
    returning.add(p)
  }
  const setTalking = (on: boolean): void => {
    if (inTalk) inTalk.talking = on
  }
  const engaged = (): Person | null => inTalk
  const personOf = (object: THREE.Object3D): string | null => {
    let o: THREE.Object3D | null = object
    while (o && !o.userData.personId) o = o.parent
    return (o?.userData.personId as string | undefined) ?? null
  }
  const whereIs = (id: string): { x: number; z: number; dir: number } | null => {
    const p = people.find((q) => q.id === id)
    if (!p) return null
    return { x: p.group.position.x, z: p.group.position.z, dir: p.walker?.dir ?? 0 }
  }

  return { group: g, people, advance, colliders, engage, disengage, setTalking, engaged, personOf, whereIs }
}
